<script setup lang="ts">
/**
 * Withdraw-requests queue container.
 *
 * Owns:
 *   - status-counts query (drives status cards + sidebar badge)
 *   - list query — defaults to the actionable slice (pending approval +
 *     awaiting settlement). The BE accepts only a single `status` query
 *     param today; when no card is picked we omit `status` server-side
 *     and filter client-side on the parsed rows. Once the BE accepts
 *     comma-separated statuses, the client-side guard can be removed.
 *   - filter drawer state + URL sync
 *
 * Drawer chrome + detail query + the three action mutations (approve,
 * mark-settled, reject) + treasury account picker + confirm dialogs live
 * in `WithdrawRequestsDetailDrawerContainer.vue` (per
 * `architecture-principles.md §20` drawer-container split).
 */
import { computed, ref } from 'vue'

import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import Message from '@uiKit/elements/Message.vue'

import { useFilterUrlSync } from '@shared/composables/useFilterUrlSync'
import { dateToIso } from '@shared/utils'

import WithdrawRequestsFilterDrawer, {
  type WithdrawRequestsFilterValues,
} from '../components/WithdrawRequestsFilterDrawer.vue'
import WithdrawRequestsList from '../components/WithdrawRequestsList.vue'
import WithdrawRequestsDetailDrawerContainer from './WithdrawRequestsDetailDrawerContainer.vue'

import { useWithdrawRequestsListQuery } from '../api/queries/useWithdrawRequestsListQuery'
import { useWithdrawRequestStatusCountsQuery } from '../api/queries/useWithdrawRequestStatusCountsQuery'

import {
  WITHDRAW_REQUEST_STATUS,
  type GetWithdrawRequestsParams,
  type WithdrawRequestRow,
  type WithdrawRequestStatus,
} from '../types/withdrawRequestsTypes'

// ─── Filters + pagination (server-side) ─────────────────────────────────────

// The actionable slice for this work queue. Drives both the page-mount
// default and the client-side fallback when no specific status card is
// active and the BE only accepts a single status query param.
const ACTIONABLE_STATUSES: WithdrawRequestStatus[] = [
  WITHDRAW_REQUEST_STATUS.PENDING_APPROVAL,
  WITHDRAW_REQUEST_STATUS.AWAITING_SETTLEMENT,
]

const currentPage = ref(1)
const pageSize = ref(10)

const filterValues = ref<WithdrawRequestsFilterValues>({
  status: null,
  fromDate: null,
  toDate: null,
  investorName: '',
  investorEmail: '',
})

const filterDrawerVisible = ref(false)

useFilterUrlSync(filterValues, {
  toQuery: (state) => ({
    status: state.status ?? undefined,
    fromDate: state.fromDate ? state.fromDate.toISOString() : undefined,
    toDate: state.toDate ? state.toDate.toISOString() : undefined,
    investorName: state.investorName || undefined,
    investorEmail: state.investorEmail || undefined,
  }),
  fromQuery: (query) => {
    const seed: Partial<WithdrawRequestsFilterValues> = {}
    if (
      typeof query.status === 'string' &&
      Object.values(WITHDRAW_REQUEST_STATUS).includes(query.status as WITHDRAW_REQUEST_STATUS)
    ) {
      seed.status = query.status as WithdrawRequestStatus
    }
    if (typeof query.fromDate === 'string') {
      const d = new Date(query.fromDate)
      if (!Number.isNaN(d.getTime())) seed.fromDate = d
    }
    if (typeof query.toDate === 'string') {
      const d = new Date(query.toDate)
      if (!Number.isNaN(d.getTime())) seed.toDate = d
    }
    if (typeof query.investorName === 'string') seed.investorName = query.investorName
    if (typeof query.investorEmail === 'string') seed.investorEmail = query.investorEmail
    return seed
  },
  onFiltersChange: () => {
    currentPage.value = 1
  },
})

const listParams = computed<GetWithdrawRequestsParams>(() => {
  const params: GetWithdrawRequestsParams = {
    page: currentPage.value,
    limit: pageSize.value,
  }
  if (filterValues.value.status) params.status = filterValues.value.status
  const fromIso = dateToIso(filterValues.value.fromDate)
  if (fromIso) params.fromDate = fromIso
  const toIsoDate = dateToIso(filterValues.value.toDate)
  if (toIsoDate) params.toDate = toIsoDate
  if (filterValues.value.investorName) params.investorName = filterValues.value.investorName
  if (filterValues.value.investorEmail) params.investorEmail = filterValues.value.investorEmail
  return params
})

const {
  data: listData,
  isLoading: listLoading,
  isFetching: listFetching,
  error: listError,
  refetch: refetchList,
} = useWithdrawRequestsListQuery(listParams)

const { data: statusCounts } = useWithdrawRequestStatusCountsQuery()

// Client-side fallback — when no status card / filter is picked, hide
// any terminal-state rows the server might return. The work queue
// never shows completed / rejected / failed / canceled rows.
const visibleRows = computed<WithdrawRequestRow[]>(() => {
  const rows = listData.value?.data ?? []
  if (filterValues.value.status) return rows
  return rows.filter((row) => ACTIONABLE_STATUSES.includes(row.status))
})

const totalRows = computed(() => {
  if (filterValues.value.status) return listData.value?.meta.total ?? 0
  const counts = statusCounts.value
  if (counts) return counts.pendingApproval + counts.awaitingSettlement
  return listData.value?.meta.total ?? 0
})

function onStatusSelect(value: WithdrawRequestStatus | null) {
  filterValues.value = { ...filterValues.value, status: value }
  currentPage.value = 1
}

function onFiltersApply(next: WithdrawRequestsFilterValues) {
  filterValues.value = { ...next }
  currentPage.value = 1
}

function onFiltersReset() {
  filterValues.value = {
    status: null,
    fromDate: null,
    toDate: null,
    investorName: '',
    investorEmail: '',
  }
  currentPage.value = 1
}

const activeFilterCount = computed(() => {
  const f = filterValues.value
  let count = 0
  if (f.status) count += 1
  if (f.fromDate) count += 1
  if (f.toDate) count += 1
  if (f.investorName) count += 1
  if (f.investorEmail) count += 1
  return count
})

const hasActiveFilters = computed(() => activeFilterCount.value > 0)

const emptyTitle = computed(() =>
  hasActiveFilters.value ? 'No withdraw requests match your filters' : 'No actionable withdraws',
)
const emptySubtitle = computed(() =>
  hasActiveFilters.value
    ? 'Clear filters or pick a different status pill above.'
    : 'New investor withdraw requests will appear here as soon as they need an approval or settlement.',
)

function onSearchChange(value: string) {
  filterValues.value = { ...filterValues.value, investorName: value }
  currentPage.value = 1
}

function openFilterDrawer() {
  filterDrawerVisible.value = true
}

function describeError(err: unknown): string {
  if (!err) return ''
  if (err instanceof Error) return err.message
  const candidate = err as { message?: unknown; code?: unknown }
  if (typeof candidate.message === 'string') return candidate.message
  if (typeof candidate.code === 'string') return candidate.code
  return 'Something went wrong. Please try again.'
}

// ─── Detail drawer handoff ──────────────────────────────────────────────────

const selectedRow = ref<WithdrawRequestRow | null>(null)
function openRow(row: WithdrawRequestRow) {
  selectedRow.value = row
}
function closeDrawer() {
  selectedRow.value = null
}
</script>

<template>
  <Box flex direction="col" gap="4">
    <Message v-if="listError" color="danger" variant="outlined" size="small">
      <Box flex justify="between" align="center" gap="3">
        <Text type="body-sm">Could not load withdraw requests. {{ describeError(listError) }}</Text>
        <Button label="Try again" variant="text" color="danger" @click="refetchList()" />
      </Box>
    </Message>

    <WithdrawRequestsList
      title="Withdraw approvals"
      :subtitle="`${totalRows} awaiting action`"
      :rows="visibleRows"
      :loading="listLoading || listFetching"
      :total="totalRows"
      :page="currentPage"
      :page-size="pageSize"
      :filter-count="activeFilterCount"
      :search="filterValues.investorName"
      :empty-title="emptyTitle"
      :empty-subtitle="emptySubtitle"
      :status-filter="filterValues.status"
      @update:page="currentPage = $event"
      @update:page-size="pageSize = $event"
      @update:status-filter="onStatusSelect"
      @update:search="onSearchChange"
      @filter-click="openFilterDrawer"
      @row-click="openRow"
    />
  </Box>

  <WithdrawRequestsFilterDrawer
    :visible="filterDrawerVisible"
    :values="filterValues"
    @update:visible="filterDrawerVisible = $event"
    @apply="onFiltersApply"
    @reset="onFiltersReset"
  />

  <WithdrawRequestsDetailDrawerContainer :selected-row="selectedRow" @close="closeDrawer" />
</template>
